<template>
  <v-card elevation="0" flat>
    <ProcessingBatchBulkImportLoadingDialog
      v-model="showLoadingDialog"
      :loading="importing"
      :title="$t('memberDataSection.processingBatchBulkImport.importingTitle')"
      :message="$t('memberDataSection.processingBatchBulkImport.importingNote')"
    />

    <v-card-text style="padding: 0 !important" class="mt-3 text-body-1 black--text">
      {{ $t('memberDataSection.processingBatchBulkImport.templateNote') }}
    </v-card-text>

    <label for="pb-template-select" class="mb-1 d-block text-body-2 black--text">
      {{ $t('memberDataSection.processingBatchBulkImport.selectTemplate') }}
    </label>
    <v-select
      id="pb-template-select"
      v-model="selectedTemplate"
      :items="templateOptions"
      item-text="text"
      item-value="value"
      outlined
      dense
      hide-details
      style="max-width: 360px"
      class="mb-1"
    ></v-select>

    <v-btn
      download
      :href="templateDownloadUrl"
      color="primary"
      class="my-3"
      large
      :disabled="importing"
    >
      {{ downloadLabel }}
      <v-icon small class="ml-2">mdi-tray-arrow-down</v-icon>
    </v-btn>

    <div
      class="dropzone"
      :class="{ 'dropzone--error': showFailedState }"
    >
      <!-- Empty / pick file -->
      <label
        v-if="!file && !showFailedState"
        class="d-flex flex-column align-center justify-center px-3 py-3"
        :class="{ active: dragging }"
        @dragenter="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="handleFileChangeEvent"
        @dragover.prevent=""
      >
        <img width="50" src="/img/xlsx-upload.svg" alt="" />
        <input
          ref="fileInput"
          class="d-none"
          type="file"
          accept=".csv,.xlsx,.xls"
          @change="handleFileChangeEvent"
        />
        <span class="font-weight-medium pt-2 text-decoration-underline blue--text darken-4">
          {{ $t('farm.selectFileToUpload') }}
        </span>
        <span>{{ $t('farm.dropHere') }}</span>
      </label>

      <!-- Failed: format or parse error -->
      <div v-else-if="showFailedState" class="dropzone-failed px-4 py-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="failed-label red--text font-weight-bold text-body-1">{{ $t('failed') }}</span>
          <v-btn icon small @click="clearFailedAndFile">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="d-flex align-center mb-2">
          <v-progress-linear
            rounded
            height="8"
            :value="failedProgress"
            color="error"
            background-color="#fde8e8"
          ></v-progress-linear>
          <span class="error--text font-weight-bold ml-2">{{ failedProgress }}%</span>
        </div>
        <p class="text-body-2 black--text mb-4 text-truncate">{{ failedFileName }}</p>
        <div class="dropzone-failed-message px-4 py-5">
          <span
            class="dropzone-failed-message-text error--text text-body-1 font-weight-medium"
            :title="importErrorMessageFull || importErrorMessage"
          >{{ importErrorMessage }}</span>
        </div>
      </div>

      <!-- File selected, ready to import -->
      <div
        v-else
        class="d-flex flex-column justify-center align-center px-3 py-3"
        style="position: relative; width: 100%; min-height: 100%;"
      >
        <img width="50" src="/img/xlsx-upload.svg" alt="" />
        <div class="mt-2 text-center">
          <span class="text-body-1">{{ file.name }}</span>
        </div>
        <v-btn
          icon
          text
          class="ma-2"
          style="position: absolute; top: 0; right: 0;"
          @click="removeFile"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <p class="pt-3 text-body-2">
      <span class="red--text">{{ $t('memberDataSection.processingBatchBulkImport.importantNote') }}</span>:
      {{ $t('memberDataSection.processingBatchBulkImport.importantNoteDetail') }}
    </p>

    <v-divider class="my-5 px-0"></v-divider>

    <v-card-actions class="px-0">
      <v-spacer></v-spacer>
      <v-btn class="mr-2" outlined color="primary" width="112" :disabled="importing" @click="cancelImport">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        :disabled="!canImportFile"
        width="112"
        color="primary"
        class="btn-11 btn-22 white--text"
        @click="runImport"
      >
        {{ $t('oma.import') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapServerImport } from "@/utils/processingBatchBulkImport";
import ProcessingBatchBulkImportService from "@/_services/ProcessingBatchBulkImportService";
import ProcessingBatchBulkImportLoadingDialog from "./ProcessingBatchBulkImportLoadingDialog.vue";

export default {
  name: "ProcessingBatchBulkImportTab",
  components: { ProcessingBatchBulkImportLoadingDialog },
  props: {
    buyingStationId: { type: [Number, String], default: null },
  },
  data() {
    return {
      dragging: false,
      file: null,
      importing: false,
      showFailedState: false,
      importErrorMessage: "",
      importErrorMessageFull: "",
      failedFileName: "",
      failedProgress: 0,
      selectedTemplate: "csv",
      templateOptions: [
        // {
        //   value: "csv",
        //   text: this.$t("memberDataSection.processingBatchBulkImport.templateCsv"),
        // },
        {
          value: "xlsx",
          text: this.$t("memberDataSection.processingBatchBulkImport.templateXls"),
        },
      ],
      acceptedFileExtensions: [".csv", ".xls", ".xlsx"],
    };
  },
  computed: {
    showLoadingDialog: {
      get() {
        return this.importing;
      },
      set(val) {
        if (!val && !this.importing) {
          this.importing = false;
        }
      },
    },
    templateDownloadUrl() {
      const base = process.env.BASE_URL || "/";
      const ext = this.selectedTemplate === "xlsx" ? "xlsx" : "csv";
      return `${base}files/processing-batch/processing-batch-bulk-import-template.${ext}`;
    },
    downloadLabel() {
      return this.selectedTemplate === "xlsx"
        ? this.$t("memberDataSection.processingBatchBulkImport.downloadXls")
        : this.$t("memberDataSection.processingBatchBulkImport.downloadCsvFormat");
    },
    canImportFile() {
      return Boolean(this.file) && !this.showFailedState && !this.importing;
    },
  },
  methods: {
    defaultFormatErrorMessage() {
      return this.$t("memberDataSection.processingBatchBulkImport.unsupportedFileFormat");
    },
    showImportError(message, fileName, progress = 100, fullMessage = "") {
      this.showFailedState = true;
      this.importErrorMessage = message || this.defaultFormatErrorMessage();
      this.importErrorMessageFull = fullMessage || this.importErrorMessage;
      this.failedFileName = fileName || "";
      this.failedProgress = progress;
      this.importing = false;
    },
    clearFailedState() {
      this.showFailedState = false;
      this.importErrorMessage = "";
      this.importErrorMessageFull = "";
      this.failedFileName = "";
      this.failedProgress = 0;
    },
    clearFailedAndFile() {
      this.clearFailedState();
      this.removeFile();
    },
    isValidExtension(file) {
      const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
      return this.acceptedFileExtensions.includes(ext);
    },
    handleFileChangeEvent(event) {
      this.dragging = false;
      let files;
      if (event instanceof DragEvent) {
        files = event.dataTransfer.files;
      } else {
        files = event.target?.files;
      }
      if (!(files instanceof FileList) || !files.length) return;

      const picked = files[0];
      this.clearFailedState();

      if (!this.isValidExtension(picked)) {
        this.file = picked;
        this.showImportError(this.defaultFormatErrorMessage(), picked.name, 100);
        return;
      }

      this.file = picked;
    },
    removeFile() {
      this.file = null;
      this.clearFailedState();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    cancelImport() {
      this.removeFile();
      this.$emit("cancelImport");
    },
    async runImport() {
      if (!this.file || this.showFailedState) return;

      if (!this.isValidExtension(this.file)) {
        this.showImportError(this.defaultFormatErrorMessage(), this.file.name, 100);
        return;
      }

      this.importing = true;

      try {
        // Upload + validate on the backend. Validation/template errors come
        // back as a resolved envelope with success === false.
        const res = await ProcessingBatchBulkImportService.validateImport(
          this.file
        );

        if (!res || res.success === false || !res.data) {
          const msg =
            (res && res.message) ||
            this.$t("memberDataSection.processingBatchBulkImport.templateErrorIntro");
          this.showImportError(msg, this.file?.name, 100, msg);
          return;
        }

        const detail = mapServerImport(res.data);
        if (res.message) {
          this.$notify({ type: "success", text: res.message });
        }
        this.$emit("importSuccess", detail);
      } catch (err) {
        console.error(err);
        const msg =
          err?.message ||
          this.$t("memberDataSection.processingBatchBulkImport.invalidFileFormat");
        this.showImportError(msg, this.file?.name, 100, msg);
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  height: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border: 2px dashed $gray;
  border-radius: 8px;
  background: #fff;
}
.dropzone--error {
  border-color: #e53935;
  border-width: 2px;
}
.dropzone label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-height: 260px;
}
.dropzone label:hover,
.dropzone label.active {
  background-color: #f5f5f5;
}
.dropzone-failed {
  width: 100%;
  min-height: 260px;
}
.dropzone-failed-message {
  border: 1px solid #e57373;
  background-color: #ffebee;
  border-radius: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.dropzone-failed-message-text {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
</style>
